import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import {
    TrendingUp,
    Clock,
    CheckCircle,
    ChevronDown,
    ChevronUp,
    Loader2,
    RefreshCw,
    BarChart3,
    Calendar,
    Search,
    AlertCircle,
    FileCheck2,
    ArrowRight,
    Receipt
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { formatCurrency, formatCurrencyFull, formatDate, formatMonth } from '../../lib/utils';
import PaymentDetailsModal from '../../components/PaymentDetailsModal';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const FinanceDashboard = () => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [timeFilter, setTimeFilter] = useState('all_time');
    const [selectedMonth, setSelectedMonth] = useState('');
    const [selectedLabel, setSelectedLabel] = useState('');
    const [labelSearchQuery, setLabelSearchQuery] = useState('');
    // Cash Flow is the only supported view for Finance now (Performance tab removed)
    const viewMode = 'cash_flow';
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    
    // Expandable sections state
    const [expandedSection, setExpandedSection] = useState(null);
    const [detailsData, setDetailsData] = useState({});
    const [detailsLoading, setDetailsLoading] = useState({});
    
    const token = localStorage.getItem('token');

    const fetchDashboard = async () => {
        setLoading(true);
        try {
            const monthType = viewMode === 'performance' ? 'transaction_month' : 'reporting_month';
            const params = new URLSearchParams({ month_type: monthType });
            // Combined filters — month and label can be applied together
            if (selectedMonth && selectedLabel) {
                params.set('time_filter', 'combo');
                params.set('month', selectedMonth);
                params.set('label_id', selectedLabel);
            } else if (selectedMonth) {
                params.set('time_filter', 'month');
                params.set('month', selectedMonth);
            } else if (selectedLabel) {
                params.set('time_filter', 'label');
                params.set('label_id', selectedLabel);
            } else {
                params.set('time_filter', 'all_time');
            }
            const url = `${API_URL}/api/dashboard/finance?${params.toString()}`;
            
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) throw new Error('Failed to fetch dashboard');
            
            const result = await response.json();
            setData(result);
        } catch (error) {
            toast.error('Failed to load finance dashboard');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDashboard();
    }, [selectedMonth, selectedLabel, viewMode]);

    // When view-mode changes, the set of available months changes (transaction vs reporting).
    // Reset month selection so we don't send a stale value the new list doesn't contain.
    useEffect(() => {
        setSelectedMonth('');
    }, [viewMode]);

    // Any filter change invalidates the cached details (so re-expand re-fetches)
    useEffect(() => {
        setDetailsData({});
    }, [selectedMonth, selectedLabel, viewMode]);

    const fetchDetails = async (type) => {
        setDetailsLoading(prev => ({ ...prev, [type]: true }));
        try {
            const monthType = viewMode === 'performance' ? 'transaction_month' : 'reporting_month';
            const params = new URLSearchParams({ month_type: monthType });
            if (selectedMonth && selectedLabel) {
                params.set('time_filter', 'combo');
                params.set('month', selectedMonth);
                params.set('label_id', selectedLabel);
            } else if (selectedMonth) {
                params.set('time_filter', 'month');
                params.set('month', selectedMonth);
            } else if (selectedLabel) {
                params.set('time_filter', 'label');
                params.set('label_id', selectedLabel);
            } else {
                params.set('time_filter', 'all_time');
            }
            const url = `${API_URL}/api/dashboard/finance/${type}-details?${params.toString()}`;
            
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) throw new Error('Failed to fetch details');
            
            const result = await response.json();
            setDetailsData(prev => ({ ...prev, [type]: result }));
        } catch (error) {
            toast.error('Failed to load details');
            console.error(error);
        } finally {
            setDetailsLoading(prev => ({ ...prev, [type]: false }));
        }
    };

    const toggleSection = (section) => {
        if (expandedSection === section) {
            setExpandedSection(null);
        } else {
            setExpandedSection(section);
            // Fetch details if not already loaded
            if (!detailsData[section]) {
                fetchDetails(section);
            }
        }
    };

    // KPI / summary cards use compact INR (Cr/L/K). Tables fall back to precise.
    const formatAmount = (amount, currency = 'INR') => formatCurrency(amount, currency);

    const handleFilterChange = (value) => {
        setTimeFilter(value);
        if (value !== 'month') setSelectedMonth('');
        if (value !== 'label') setSelectedLabel('');
        // Reset expanded sections when filter changes
        setExpandedSection(null);
        setDetailsData({});
    };

    // Filter labels based on search query
    const filteredLabels = data?.filters?.available_labels?.filter(label => 
        label.name.toLowerCase().includes(labelSearchQuery.toLowerCase()) ||
        label.code?.toLowerCase().includes(labelSearchQuery.toLowerCase())
    ) || [];

    // Generate month options for date picker from backend's available_months (kept for compat, unused)
    const generateMonthOptions = () => (data?.filters?.available_months || []).map(m => ({ label: m, value: m }));

    if (loading && !data) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-heading font-bold">Finance Dashboard</h1>
                    <p className="text-muted-foreground">
                        Revenue &amp; Invoices by reporting month (cash flow)
                    </p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    <Button onClick={fetchDashboard} variant="outline" size="sm">
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Refresh
                    </Button>
                </div>
            </div>

            {/* Filters — Month + Label can be combined */}
            <Card className="bg-card/50 border-border/40">
                <CardContent className="pt-6">
                    <div className="flex flex-wrap items-center gap-4">
                        <div className="flex items-center gap-2">
                            <BarChart3 className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm font-medium">Filter:</span>
                        </div>
                        
                        {/* Month filter */}
                        <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-muted-foreground" />
                            <Select value={selectedMonth || 'all'} onValueChange={(v) => setSelectedMonth(v === 'all' ? '' : v)}>
                                <SelectTrigger className="w-[200px]" data-testid="finance-month-select">
                                    <SelectValue placeholder={viewMode === 'performance' ? 'All Transaction Months' : 'All Reporting Months'} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{viewMode === 'performance' ? 'All Transaction Months' : 'All Reporting Months'}</SelectItem>
                                    {(data?.filters?.available_months || []).map((m) => (
                                        <SelectItem key={m} value={m}>
                                            {formatMonth(m)}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Label filter */}
                        <div className="relative flex-1 min-w-[250px] max-w-[400px]">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                placeholder={selectedLabel ? 'Label selected (click × to clear)' : 'All labels — type to search…'}
                                value={labelSearchQuery}
                                onChange={(e) => {
                                    setLabelSearchQuery(e.target.value);
                                    if (e.target.value === '') setSelectedLabel('');
                                }}
                                className="pl-10 pr-8"
                                data-testid="finance-label-search"
                            />
                            {(selectedLabel || labelSearchQuery) && (
                                <button
                                    type="button"
                                    onClick={() => { setSelectedLabel(''); setLabelSearchQuery(''); }}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                    data-testid="clear-label-filter"
                                >
                                    ×
                                </button>
                            )}
                            {labelSearchQuery && !selectedLabel && filteredLabels.length > 0 && (
                                <div className="absolute top-full mt-1 w-full bg-card border border-border rounded-md shadow-lg max-h-60 overflow-y-auto z-50">
                                    {filteredLabels.slice(0, 30).map((label) => (
                                        <div
                                            key={label.id}
                                            className="px-4 py-2 cursor-pointer hover:bg-muted/50 transition-colors"
                                            onClick={() => {
                                                setSelectedLabel(label.id);
                                                setLabelSearchQuery(label.name);
                                            }}
                                        >
                                            <div className="font-medium">{label.name}</div>
                                            <div className="text-xs text-muted-foreground">{label.code}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Active filter chips */}
                        {(selectedMonth || selectedLabel) && (
                            <div className="flex items-center gap-2 flex-wrap">
                                {selectedMonth && (
                                    <Badge variant="outline" className="gap-1 bg-primary/10 border-primary/30 text-primary">
                                        {formatMonth(selectedMonth)}
                                        <button onClick={() => setSelectedMonth('')} className="ml-1 hover:text-destructive" data-testid="clear-month-chip">×</button>
                                    </Badge>
                                )}
                                {selectedLabel && (
                                    <Badge variant="outline" className="gap-1 bg-secondary/10 border-secondary/30 text-secondary">
                                        {data?.filters?.available_labels?.find(l => l.id === selectedLabel)?.name || 'Label'}
                                        <button onClick={() => { setSelectedLabel(''); setLabelSearchQuery(''); }} className="ml-1 hover:text-destructive" data-testid="clear-label-chip">×</button>
                                    </Badge>
                                )}
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => { setSelectedMonth(''); setSelectedLabel(''); setLabelSearchQuery(''); }}
                                    className="text-xs"
                                    data-testid="clear-all-filters"
                                >
                                    Clear all
                                </Button>
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Key Metrics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Total Revenue Generated (Gross) */}
                <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-500/20" data-testid="finance-card-gross-revenue">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                            <TrendingUp className="w-4 h-4 text-green-500" />
                            Gross Revenue
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-2">
                            <div className="flex items-baseline gap-2">
                                <span className="text-3xl font-bold text-green-500">
                                    {formatAmount(data?.metrics?.total_revenue?.gross_inr ?? data?.metrics?.total_revenue?.inr)}
                                </span>
                            </div>
                            <div className="text-xs text-muted-foreground">
                                {data?.metrics?.total_revenue?.records?.toLocaleString() || 0} revenue records
                            </div>
                            <Button 
                                variant="outline" 
                                size="sm" 
                                className="mt-3 w-full border-green-500/30 text-green-500 hover:bg-green-500/10"
                                onClick={() => toggleSection('revenue')}
                            >
                                {expandedSection === 'revenue' ? (
                                    <><ChevronUp className="w-4 h-4 mr-2" /> Hide Details</>
                                ) : (
                                    <><ChevronDown className="w-4 h-4 mr-2" /> View Details</>
                                )}
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {/* Net Revenue (platform's earnings after label payouts) */}
                <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border-emerald-500/20" data-testid="finance-card-net-revenue">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                            <TrendingUp className="w-4 h-4 text-emerald-400" />
                            Net Revenue
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-2">
                            <div className="flex items-baseline gap-2">
                                <span className="text-3xl font-bold text-emerald-400">
                                    {formatAmount(data?.metrics?.total_revenue?.net_inr)}
                                </span>
                            </div>
                            <div className="text-xs text-muted-foreground">
                                After {formatAmount(data?.metrics?.total_revenue?.label_payout_inr)} label share
                            </div>
                            <div className="text-xs text-muted-foreground">
                                Company's earnings
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Total Paid Amount */}
                <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-blue-500" />
                            Total Paid Amount
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-2">
                            <div className="flex items-baseline gap-2">
                                <span className="text-3xl font-bold text-blue-500">
                                    {formatAmount(data?.metrics?.total_paid?.inr)}
                                </span>
                            </div>
                            <div className="text-xs text-muted-foreground">
                                {data?.metrics?.total_paid?.count || 0} paid invoices
                            </div>
                            <Button 
                                variant="outline" 
                                size="sm" 
                                className="mt-3 w-full border-blue-500/30 text-blue-500 hover:bg-blue-500/10"
                                onClick={() => toggleSection('paid')}
                            >
                                {expandedSection === 'paid' ? (
                                    <><ChevronUp className="w-4 h-4 mr-2" /> Hide Details</>
                                ) : (
                                    <><ChevronDown className="w-4 h-4 mr-2" /> View Details</>
                                )}
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {/* Total Pending Amount (net pending to labels = Earned − Paid) */}
                <Card className="bg-gradient-to-br from-yellow-500/10 to-yellow-600/5 border-yellow-500/20">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                            <Clock className="w-4 h-4 text-yellow-500" />
                            Total Pending to Labels
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-2">
                            <div className="flex items-baseline gap-2">
                                <span className="text-3xl font-bold text-yellow-500">
                                    {formatAmount(data?.metrics?.total_pending?.inr)}
                                </span>
                            </div>
                            <div className="text-xs text-muted-foreground">
                                Earned {formatAmount(data?.metrics?.total_pending?.earned_net_inr)} — Paid {formatAmount(data?.metrics?.total_paid?.inr)}
                            </div>
                            <div className="grid grid-cols-2 gap-2 pt-2 text-xs">
                                <div className="rounded-md bg-yellow-500/10 p-2 border border-yellow-500/20" data-testid="pending-invoiced">
                                    <div className="text-[10px] uppercase tracking-wider text-yellow-500/80">Invoiced, awaiting action</div>
                                    <div className="font-mono font-semibold text-yellow-500">
                                        {formatAmount(data?.metrics?.total_pending?.invoiced_inr)}
                                    </div>
                                    <div className="text-muted-foreground">
                                        {data?.metrics?.total_pending?.invoiced_count || 0} invoice(s)
                                    </div>
                                </div>
                                <div className="rounded-md bg-orange-500/10 p-2 border border-orange-500/20" data-testid="pending-unbilled">
                                    <div className="text-[10px] uppercase tracking-wider text-orange-500/80">Not yet invoiced</div>
                                    <div className="font-mono font-semibold text-orange-500">
                                        {formatAmount(data?.metrics?.total_pending?.unbilled_inr)}
                                    </div>
                                    <div className="text-muted-foreground">awaiting label to raise invoice</div>
                                </div>
                            </div>
                            <Button 
                                variant="outline" 
                                size="sm" 
                                className="mt-3 w-full border-yellow-500/30 text-yellow-500 hover:bg-yellow-500/10"
                                onClick={() => toggleSection('pending')}
                            >
                                {expandedSection === 'pending' ? (
                                    <><ChevronUp className="w-4 h-4 mr-2" /> Hide Details</>
                                ) : (
                                    <><ChevronDown className="w-4 h-4 mr-2" /> View Details</>
                                )}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Pending Tasks — what needs Finance action */}
            <Card className="bg-card/50 border-border/40" data-testid="finance-pending-tasks">
                <CardHeader className="pb-3">
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                                <AlertCircle className="w-5 h-5 text-yellow-500" />
                            </div>
                            <div>
                                <CardTitle className="text-lg">Pending Tasks</CardTitle>
                                <p className="text-xs text-muted-foreground">
                                    Invoices awaiting review, approval, or payment
                                </p>
                            </div>
                        </div>
                        <Button asChild variant="ghost" size="sm" className="gap-1" data-testid="go-to-invoices">
                            <Link to="/admin/invoices">
                                All invoices <ArrowRight className="w-4 h-4" />
                            </Link>
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    {/* Status buckets */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                        <div className="p-4 rounded-lg bg-yellow-500/5 border border-yellow-500/20" data-testid="bucket-pending-finance">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-medium uppercase tracking-wider text-yellow-500">Awaiting Finance</span>
                                <Clock className="w-4 h-4 text-yellow-500" />
                            </div>
                            <div className="text-2xl font-bold">
                                {data?.metrics?.pending_by_status?.pending_finance?.count || 0}
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">
                                {formatCurrency(data?.metrics?.pending_by_status?.pending_finance?.inr || 0, 'INR')}
                            </div>
                        </div>
                        <div className="p-4 rounded-lg bg-purple-500/5 border border-purple-500/20" data-testid="bucket-pending-founder">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-medium uppercase tracking-wider text-purple-500">Awaiting Founder</span>
                                <FileCheck2 className="w-4 h-4 text-purple-500" />
                            </div>
                            <div className="text-2xl font-bold">
                                {data?.metrics?.pending_by_status?.pending_founder?.count || 0}
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">
                                {formatCurrency(data?.metrics?.pending_by_status?.pending_founder?.inr || 0, 'INR')}
                            </div>
                        </div>
                        <div className="p-4 rounded-lg bg-blue-500/5 border border-blue-500/20" data-testid="bucket-approved">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-medium uppercase tracking-wider text-blue-500">Approved — Ready to Pay</span>
                                <CheckCircle className="w-4 h-4 text-blue-500" />
                            </div>
                            <div className="text-2xl font-bold">
                                {data?.metrics?.pending_by_status?.approved?.count || 0}
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">
                                {formatCurrency(data?.metrics?.pending_by_status?.approved?.inr || 0, 'INR')}
                            </div>
                        </div>
                    </div>

                    {/* Recent pending tasks list */}
                    {data?.pending_tasks && data.pending_tasks.length > 0 ? (
                        <div className="rounded-lg border border-border/40 overflow-hidden">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-muted/20 hover:bg-muted/20">
                                        <TableHead className="text-xs uppercase tracking-wider">Invoice</TableHead>
                                        <TableHead className="text-xs uppercase tracking-wider">Label</TableHead>
                                        <TableHead className="text-xs uppercase tracking-wider">Month</TableHead>
                                        <TableHead className="text-xs uppercase tracking-wider text-right">Amount</TableHead>
                                        <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
                                        <TableHead className="text-xs uppercase tracking-wider">Created</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {data.pending_tasks.map((inv) => {
                                        const statusMap = {
                                            pending_finance: { label: 'Awaiting Finance', cls: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/30' },
                                            pending_founder: { label: 'Awaiting Founder', cls: 'bg-purple-500/10 text-purple-500 border-purple-500/30' },
                                            approved: { label: 'Ready to Pay', cls: 'bg-blue-500/10 text-blue-500 border-blue-500/30' }
                                        };
                                        const s = statusMap[inv.status] || { label: inv.status, cls: '' };
                                        return (
                                            <TableRow key={inv.id} data-testid={`pending-task-${inv.id}`}>
                                                <TableCell className="font-mono text-xs">{inv.invoice_number}</TableCell>
                                                <TableCell>{inv.label_name}</TableCell>
                                                <TableCell className="text-sm">{formatMonth(inv.month)}</TableCell>
                                                <TableCell className="text-right font-mono">
                                                    {formatCurrencyFull(inv.amount, inv.currency || 'INR')}
                                                </TableCell>
                                                <TableCell>
                                                    <Badge variant="outline" className={s.cls}>
                                                        {s.label}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="text-xs text-muted-foreground">
                                                    {formatDate(inv.created_at)}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    ) : (
                        <div className="text-center py-8 text-muted-foreground">
                            <CheckCircle className="w-10 h-10 mx-auto mb-2 text-green-500/60" />
                            <p className="text-sm">All caught up — no pending tasks!</p>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Expandable Detail Sections */}
            {expandedSection === 'revenue' && (
                <Card className="border-green-500/30 bg-green-500/5">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-green-500">
                            <TrendingUp className="w-5 h-5" />
                            Revenue Breakdown Details
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {detailsLoading.revenue ? (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 className="w-8 h-8 animate-spin text-primary" />
                            </div>
                        ) : detailsData.revenue && (
                            <div className="space-y-6">
                                <div className="text-sm text-muted-foreground">
                                    Total Records: {detailsData.revenue.total_records?.toLocaleString() || 0}
                                </div>
                                
                                {/* Label Breakdown */}
                                <div>
                                    <h3 className="font-semibold mb-3">By Label</h3>
                                    <div className="border rounded-lg overflow-hidden">
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>Label</TableHead>
                                                    <TableHead className="text-right">Revenue (INR)</TableHead>
                                                    <TableHead className="text-right">Revenue (USD)</TableHead>
                                                    <TableHead className="text-right">Records</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {detailsData.revenue.label_breakdown?.map((item, idx) => (
                                                    <TableRow key={idx}>
                                                        <TableCell className="font-medium">{item.label_name || 'Unknown'}</TableCell>
                                                        <TableCell className="text-right">{formatCurrencyFull(item.total_inr)}</TableCell>
                                                        <TableCell className="text-right">{formatCurrencyFull(item.total_usd, 'USD')}</TableCell>
                                                        <TableCell className="text-right">{item.record_count?.toLocaleString() || 0}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </div>

                                {/* Platform Breakdown */}
                                <div>
                                    <h3 className="font-semibold mb-3">By Platform</h3>
                                    <div className="border rounded-lg overflow-hidden">
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>Platform</TableHead>
                                                    <TableHead className="text-right">Revenue (INR)</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {detailsData.revenue.platform_breakdown?.map((item, idx) => (
                                                    <TableRow key={idx}>
                                                        <TableCell className="font-medium">{item._id || 'Unknown'}</TableCell>
                                                        <TableCell className="text-right">{formatCurrencyFull(item.total_inr)}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {expandedSection === 'paid' && (
                <Card className="border-blue-500/30 bg-blue-500/5">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-blue-500">
                            <CheckCircle className="w-5 h-5" />
                            Paid Invoices Details
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {detailsLoading.paid ? (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 className="w-8 h-8 animate-spin text-primary" />
                            </div>
                        ) : detailsData.paid && (
                            <div className="space-y-6">
                                <div className="text-sm text-muted-foreground">
                                    Total Paid Invoices: {detailsData.paid.total || 0}
                                </div>
                                
                                {/* Label Summary */}
                                {detailsData.paid.label_breakdown?.length > 0 && (
                                    <div>
                                        <h3 className="font-semibold mb-3">Summary by Label</h3>
                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                            {detailsData.paid.label_breakdown.map((item, idx) => (
                                                <div key={idx} className="p-3 bg-muted/30 rounded-lg">
                                                    <div className="font-medium text-sm">{item.label_name}</div>
                                                    <div className="text-lg font-bold text-blue-500">
                                                        {item._id.currency === 'INR' ? '₹' : '$'}{item.total_amount?.toLocaleString()}
                                                    </div>
                                                    <div className="text-xs text-muted-foreground">{item.invoice_count} invoices</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Invoice List */}
                                <div>
                                    <h3 className="font-semibold mb-3">Invoice List</h3>
                                    <div className="border rounded-lg overflow-hidden">
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>Invoice #</TableHead>
                                                    <TableHead>Label</TableHead>
                                                    <TableHead>Month</TableHead>
                                                    <TableHead className="text-right">Amount</TableHead>
                                                    <TableHead>Paid on</TableHead>
                                                    <TableHead className="text-right">Action</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {detailsData.paid.invoices?.map((invoice) => (
                                                    <TableRow key={invoice.id}>
                                                        <TableCell className="font-mono text-sm">{invoice.invoice_number}</TableCell>
                                                        <TableCell>{invoice.label_name}</TableCell>
                                                        <TableCell>{formatMonth(invoice.month)}</TableCell>
                                                        <TableCell className="text-right font-semibold">
                                                            {formatCurrencyFull(invoice.amount, invoice.currency || 'INR')}
                                                        </TableCell>
                                                        <TableCell className="text-xs text-muted-foreground">
                                                            {invoice.paid_at ? formatDate(invoice.paid_at) : '—'}
                                                        </TableCell>
                                                        <TableCell className="text-right">
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                onClick={() => setSelectedInvoice(invoice)}
                                                                data-testid={`view-payment-${invoice.id}`}
                                                            >
                                                                <Receipt className="w-3 h-3 mr-1" />
                                                                Payment
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {expandedSection === 'pending' && (
                <Card className="border-yellow-500/30 bg-yellow-500/5">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-yellow-500">
                            <Clock className="w-5 h-5" />
                            Pending Details — Labels Awaiting Payout
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {detailsLoading.pending ? (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 className="w-8 h-8 animate-spin text-primary" />
                            </div>
                        ) : detailsData.pending && (
                            <div className="space-y-6">
                                {/* Per-label unbilled + invoiced pending */}
                                <div>
                                    <h3 className="font-semibold mb-3">Per-Label Pending</h3>
                                    {(!detailsData.pending.unbilled_breakdown || detailsData.pending.unbilled_breakdown.length === 0) ? (
                                        <div className="text-sm text-muted-foreground p-4 bg-muted/20 rounded-md">
                                            No labels with pending revenue for this filter.
                                        </div>
                                    ) : (
                                        <div className="border rounded-lg overflow-hidden">
                                            <Table>
                                                <TableHeader>
                                                    <TableRow className="bg-muted/20 hover:bg-muted/20">
                                                        <TableHead>Label</TableHead>
                                                        <TableHead className="text-right">Gross Revenue</TableHead>
                                                        <TableHead className="text-right">Share %</TableHead>
                                                        <TableHead className="text-right">Earned (Net)</TableHead>
                                                        <TableHead className="text-right">Paid</TableHead>
                                                        <TableHead className="text-right">Invoiced, awaiting</TableHead>
                                                        <TableHead className="text-right">Not yet invoiced</TableHead>
                                                        <TableHead className="text-right">Total Pending</TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {detailsData.pending.unbilled_breakdown.map((row) => (
                                                        <TableRow key={row.label_id} data-testid={`pending-label-${row.label_id}`}>
                                                            <TableCell className="font-medium">{row.label_name}</TableCell>
                                                            <TableCell className="text-right font-mono">{formatCurrencyFull(row.gross_inr, 'INR')}</TableCell>
                                                            <TableCell className="text-right font-mono text-xs text-muted-foreground">{row.share_percentage}%</TableCell>
                                                            <TableCell className="text-right font-mono">{formatCurrencyFull(row.earned_net_inr, 'INR')}</TableCell>
                                                            <TableCell className="text-right font-mono text-blue-500">{formatCurrencyFull(row.paid_inr, 'INR')}</TableCell>
                                                            <TableCell className="text-right font-mono text-yellow-500">
                                                                {formatCurrencyFull(row.invoiced_pending_inr, 'INR')}
                                                                {row.invoiced_pending_count > 0 && (
                                                                    <span className="text-xs text-muted-foreground ml-1">({row.invoiced_pending_count})</span>
                                                                )}
                                                            </TableCell>
                                                            <TableCell className="text-right font-mono text-orange-500">{formatCurrencyFull(row.unbilled_pending_inr, 'INR')}</TableCell>
                                                            <TableCell className="text-right font-mono font-semibold text-yellow-500">
                                                                {formatCurrencyFull(
                                                                    (row.invoiced_pending_inr || 0) + (row.unbilled_pending_inr || 0),
                                                                    'INR'
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </div>
                                    )}
                                </div>

                                {/* Invoices currently in workflow */}
                                {detailsData.pending.invoices?.length > 0 && (
                                    <div>
                                        <h3 className="font-semibold mb-3">Invoices Currently in Workflow</h3>
                                        <div className="border rounded-lg overflow-hidden">
                                            <Table>
                                                <TableHeader>
                                                    <TableRow>
                                                        <TableHead>Invoice #</TableHead>
                                                        <TableHead>Label</TableHead>
                                                        <TableHead>Month</TableHead>
                                                        <TableHead className="text-right">Amount</TableHead>
                                                        <TableHead>Status</TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {detailsData.pending.invoices.map((invoice) => {
                                                        const statusMap = {
                                                            pending_finance: { label: 'Awaiting Finance', cls: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/30' },
                                                            pending_founder: { label: 'Awaiting Founder', cls: 'bg-purple-500/10 text-purple-500 border-purple-500/30' },
                                                            approved: { label: 'Ready to Pay', cls: 'bg-blue-500/10 text-blue-500 border-blue-500/30' }
                                                        };
                                                        const s = statusMap[invoice.status] || { label: invoice.status, cls: '' };
                                                        return (
                                                            <TableRow key={invoice.id}>
                                                                <TableCell className="font-mono text-sm">{invoice.invoice_number}</TableCell>
                                                                <TableCell>{invoice.label_name}</TableCell>
                                                                <TableCell>{formatMonth(invoice.month)}</TableCell>
                                                                <TableCell className="text-right font-mono">
                                                                    {formatCurrency(invoice.amount, invoice.currency || 'INR')}
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Badge variant="outline" className={s.cls}>{s.label}</Badge>
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    })}
                                                </TableBody>
                                            </Table>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-card/50 border-border/40">
                    <CardContent className="pt-4">
                        <div className="text-2xl font-bold">{data?.filters?.available_labels?.length || 0}</div>
                        <div className="text-sm text-muted-foreground">Total Labels</div>
                    </CardContent>
                </Card>
                <Card className="bg-card/50 border-border/40">
                    <CardContent className="pt-4">
                        <div className="text-2xl font-bold">{data?.filters?.available_months?.length || 0}</div>
                        <div className="text-sm text-muted-foreground">Reporting Months</div>
                    </CardContent>
                </Card>
                <Card className="bg-card/50 border-border/40">
                    <CardContent className="pt-4">
                        <div className="text-2xl font-bold text-green-500">
                            {(data?.metrics?.total_paid?.count || 0) + (data?.metrics?.total_pending?.count || 0)}
                        </div>
                        <div className="text-sm text-muted-foreground">Total Invoices</div>
                    </CardContent>
                </Card>
            </div>

            {/* Payment details modal */}
            <PaymentDetailsModal
                invoice={selectedInvoice}
                open={!!selectedInvoice}
                onOpenChange={(open) => { if (!open) setSelectedInvoice(null); }}
            />
        </div>
    );
};

export default FinanceDashboard;
